import { useState, useCallback, useRef, useEffect } from 'react';

export interface UseDialogStateReturn<T = null> {
  /** Whether dialog is open */
  isOpen: boolean;
  /** Selected item (null when creating new) */
  item: T | null;
  /** Open dialog with an item (for edit/view) */
  open: (item?: T | null) => void;
  /** Close dialog and reset item */
  close: () => void;
  /** Toggle dialog state */
  toggle: () => void;
  /** Whether dialog is in edit mode (has item) */
  isEditMode: boolean;
  /** Whether dialog is in create mode (no item) */
  isCreateMode: boolean;
}

/**
 * Hook for managing dialog open/close state with an optional selected item.
 * Replaces the common pattern of:
 * - const [dialogOpen, setDialogOpen] = useState(false);
 * - const [selectedItem, setSelectedItem] = useState<T | null>(null);
 *
 * @example
 * // Basic usage
 * const { isOpen, item, open, close } = useDialogState<UserDto>();
 *
 * // Open for create
 * <Button onClick={() => open()}>Create</Button>
 *
 * // Open for edit
 * <Button onClick={() => open(user)}>Edit</Button>
 *
 * // In dialog
 * <Dialog open={isOpen} onClose={close}>
 *   <DialogTitle>{item ? 'Edit User' : 'Create User'}</DialogTitle>
 * </Dialog>
 *
 * @example
 * // With confirm dialog
 * const deleteDialog = useDialogState<number>(); // stores id to delete
 *
 * <Button onClick={() => deleteDialog.open(item.id)}>Delete</Button>
 *
 * <Dialog open={deleteDialog.isOpen}>
 *   <Button onClick={async () => {
 *     await api.delete(deleteDialog.item);
 *     deleteDialog.close();
 *   }}>Confirm</Button>
 * </Dialog>
 */
export const useDialogState = <T = null>(): UseDialogStateReturn<T> => {
  const [isOpen, setIsOpen] = useState(false);
  const [item, setItem] = useState<T | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const open = useCallback((selectedItem?: T | null) => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setItem(selectedItem ?? null);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    // Clear item after a short delay to prevent UI flicker during close animation
    closeTimerRef.current = setTimeout(() => setItem(null), 150);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  return {
    isOpen,
    item,
    open,
    close,
    toggle,
    isEditMode: item !== null,
    isCreateMode: item === null,
  };
};

/**
 * Hook for managing multiple dialogs with a single state.
 * Useful when a page has many dialogs (create, edit, delete, view, etc.)
 *
 * @example
 * const dialogs = useMultiDialogState<{
 *   create: null;
 *   edit: UserDto;
 *   delete: number;
 *   view: UserDto;
 * }>();
 *
 * <Button onClick={() => dialogs.open('create')}>Create</Button>
 * <Button onClick={() => dialogs.open('edit', user)}>Edit</Button>
 * <Button onClick={() => dialogs.open('delete', user.id)}>Delete</Button>
 *
 * <CreateDialog open={dialogs.isOpen('create')} onClose={dialogs.close} />
 * <EditDialog open={dialogs.isOpen('edit')} item={dialogs.get('edit')} onClose={dialogs.close} />
 */
export interface DialogTypeMap {
  [key: string]: any;
}

export interface UseMultiDialogStateReturn<T extends DialogTypeMap> {
  /** Current open dialog name (null if none) */
  current: keyof T | null;
  /** Get item for a specific dialog type */
  get: <K extends keyof T>(type: K) => T[K] | null;
  /** Check if a specific dialog is open */
  isOpen: (type: keyof T) => boolean;
  /** Open a specific dialog with optional item */
  open: <K extends keyof T>(type: K, item?: T[K]) => void;
  /** Close current dialog */
  close: () => void;
}

export const useMultiDialogState = <T extends DialogTypeMap>(): UseMultiDialogStateReturn<T> => {
  const [current, setCurrent] = useState<keyof T | null>(null);
  const [items, setItems] = useState<Partial<T>>({});
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const get = useCallback(<K extends keyof T>(type: K): T[K] | null => {
    return (items[type] as T[K]) ?? null;
  }, [items]);

  const isOpen = useCallback((type: keyof T): boolean => {
    return current === type;
  }, [current]);

  const open = useCallback(<K extends keyof T>(type: K, item?: T[K]) => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setItems(prev => ({ ...prev, [type]: item ?? null }));
    setCurrent(type);
  }, []);

  const close = useCallback(() => {
    setCurrent(null);
    // Clear items after animation
    closeTimerRef.current = setTimeout(() => setItems({}), 150);
  }, []);

  return {
    current,
    get,
    isOpen,
    open,
    close,
  };
};

export default useDialogState;
